import { useCallback, useEffect, useState } from 'react';
import { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

import { useClientProps } from '../../ProfileMenu/providers/ClientPropsProvider';
import { useProfileMenuV2 } from '../../ProfileMenu/providers/ProfileMenuV2Provider';
import { useIdentityAccountStateInternal } from '../../providers/IdentityAccountStateProvider';
import { RCIDChangeEvent } from '../RCIDChangeEvent';
import { resourceContextStore } from '../ResourceContextStore';

export function useResourceContext() {
  const { resources } = useProfileMenuV2();
  const { onEntitySwitch } = useClientProps();

  const { setEntityResourceList, setInitialized } = useIdentityAccountStateInternal();
  const { config } = useClientProps();

  const [activeResourceIdState, setActiveResourceIdState] = useState<string | undefined>(
    resourceContextStore.getActiveResourceId(),
  );

  const [activeResource, setActiveResource] = useState<CoinbaseProfileMenuResource | undefined>();
  const [needsSelection, setNeedsSelection] = useState(false);

  const blockingEntityPickerEnabled = config?.blockingEntityPickerEnabled === true;

  const handleResourceSwitch = useCallback(
    (accountId: string) => {
      resourceContextStore.persistActiveResource(accountId);
      onEntitySwitch?.(accountId);
    },
    [onEntitySwitch],
  );

  const handleBlockingSelection = useCallback(
    async (resourceId: string) => {
      const didPersist = await resourceContextStore.persistActiveResource(resourceId);
      if (!didPersist) return;

      try {
        setNeedsSelection(false);
        const resource = await resourceContextStore.getResourceFromId(resourceId);
        setActiveResource(resource);
        setActiveResourceIdState(resourceId);
        setInitialized(true);
      } catch {
        // getResourceFromId operates on in-memory data — failures here are
        // unexpected but non-fatal. Fall through: the picker remains visible
        // so the user can retry.
        return;
      }

      onEntitySwitch?.(resourceId);
    },
    [setInitialized, onEntitySwitch],
  );

  const initialize = useCallback(async () => {
    const result = await resourceContextStore.initializeAsync();

    if (result.needsSelection && blockingEntityPickerEnabled) {
      setNeedsSelection(true);
      setActiveResourceIdState(undefined);
      setActiveResource(undefined);
      return;
    }

    // If needsSelection but flag is off, fall back to first resource (legacy behavior).
    // Use selectWithoutPersist so the RCID is set in memory (for the middleware)
    // but NOT written to sessionStorage. This way, if the experiment resolves
    // on a later render, initializeAsync() won't find a stored RCID and will
    // correctly return needsSelection: true for the picker.
    if (result.needsSelection && result.resources?.[0]) {
      await resourceContextStore.selectWithoutPersist(result.resources[0].identityAccountId);
    }

    const resource = await resourceContextStore.getResourceFromId(
      resourceContextStore.getActiveResourceId(),
    );
    setActiveResource(resource);
    setActiveResourceIdState(resourceContextStore.getActiveResourceId());
    setInitialized(true);
  }, [setInitialized, blockingEntityPickerEnabled]);

  useEffect(() => {
    void initialize();
  }, [initialize, resources]);

  useEffect(() => {
    const handleRCIDChange = (e: Event) => {
      const event = e as RCIDChangeEvent;
      setActiveResourceIdState(event.detail);
    };

    window.addEventListener('RCID_CHANGE', handleRCIDChange);

    return () => window.removeEventListener('RCID_CHANGE', handleRCIDChange);
  }, []);

  useEffect(() => {
    setEntityResourceList(resources);
  }, [resources, setEntityResourceList]);

  useEffect(() => {
    if (config?.orgSwitcherEnabled === false) {
      setEntityResourceList(undefined);
      setInitialized(false);
    }
  }, [config?.orgSwitcherEnabled, resources, setEntityResourceList, setInitialized]);

  return {
    handleResourceSwitch,
    handleBlockingSelection,
    activeResourceId: activeResourceIdState,
    activeResource,
    resources,
    needsSelection,
  };
}
